import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useAuth } from "./AuthContext";
import { api } from "./api";
import BrandMark from "./BrandMark";
import { useStackTables } from "./useStackTables";
import { PLATFORMS } from "./catalog";
import {
  LayoutDashboard, Gamepad2, Boxes, Layers, Radio, ListOrdered, Settings,
  LogOut, Menu, X, ExternalLink, Compass, Users, Link2, ShieldCheck,
} from "lucide-react";

const BOTTOM = [
  { to: "/admin", end: true, label: "Visão", icon: LayoutDashboard, testid: "bnav-dashboard" },
  { to: "/admin/traffic", label: "Tráfego", icon: Compass, testid: "bnav-traffic" },
  { to: "/admin/signals", label: "Sinais", icon: Radio, testid: "bnav-signals" },
  { to: "/admin/events", label: "Eventos", icon: ListOrdered, testid: "bnav-events" },
];

function BottomNav({ onMenu, alerts }) {
  return (
    <nav className="mia-bottom-nav lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-[var(--border)] grid grid-cols-5" aria-label="Navegação rápida" data-testid="bottom-nav">
      {BOTTOM.map(({ to, end, label, icon: Icon, testid }) => (
        <NavLink key={to} to={to} end={end} data-testid={testid}
          className={({ isActive }) => `relative flex flex-col items-center justify-center gap-1 text-[11px] transition-colors ${isActive ? "text-[var(--text)]" : "text-[var(--muted)]"}`}>
          {({ isActive }) => (
            <>
              {isActive && <motion.span layoutId="bnav-ind" className="absolute top-0 h-[2px] w-8 rounded-full bg-[var(--accent)]" />}
              <Icon size={19} aria-hidden="true" className={isActive ? "text-[var(--accent-ink)]" : ""} />
              {label}
            </>
          )}
        </NavLink>
      ))}
      <button type="button" onClick={onMenu} data-testid="bnav-menu" className="relative flex flex-col items-center justify-center gap-1 text-[11px] text-[var(--muted)]">
        <Menu size={19} aria-hidden="true" />Menu
        {alerts > 0 && <span className="absolute top-2 right-[calc(50%-18px)] w-2 h-2 rounded-full bg-[var(--bad)]" aria-label={`${alerts} alertas abertos`} />}
      </button>
    </nav>
  );
}

const NAV = [
  { group: "Análise", items: [
    { to: "/admin", end: true, label: "Visão geral", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/admin/traffic", label: "Tráfego e campanhas", icon: Compass, testid: "nav-traffic" },
    { to: "/admin/visitors", label: "Visitantes", icon: Users, testid: "nav-visitors" },
    { to: "/admin/signals", label: "Sinais", icon: Radio, testid: "nav-signals" },
  ] },
  { group: "Catálogo", items: [
    { to: "/admin/games", label: "Jogos", icon: Gamepad2, testid: "nav-games" },
    { to: "/admin/platforms", label: "Plataformas", icon: Layers, testid: "nav-platforms" },
    { to: "/admin/providers", label: "Provedores", icon: Boxes, testid: "nav-providers" },
  ] },
  { group: "Operação", items: [
    { to: "/admin/links", label: "Monitor de links", icon: Link2, testid: "nav-links", alerts: true },
    { to: "/admin/events", label: "Histórico de eventos", icon: ListOrdered, testid: "nav-events" },
    { to: "/admin/users", label: "Usuários e acessos", icon: ShieldCheck, testid: "nav-users", ownerOnly: true },
    { to: "/admin/settings", label: "Configurações", icon: Settings, testid: "nav-settings" },
  ] },
];

function useOpenAlerts() {
  const [n, setN] = useState(0);
  const { pathname } = useLocation();
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/admin/links/alerts").then(({ data }) => alive && setN(data.alerts.filter((a) => a.status === "open").length)).catch(() => {});
    load();
    const t = setInterval(load, 120000);
    return () => { alive = false; clearInterval(t); };
  }, [pathname]);
  return n;
}

function Sidebar({ onNavigate, alerts, user, onLogout }) {
  return (
    <div className="flex flex-col h-full">
      <div className="h-14 flex items-center px-4 border-b border-[var(--border)]">
        <BrandMark sub="Painel administrativo" />
      </div>
      <nav className="flex-1 overflow-y-auto mia-scroll py-3" aria-label="Navegação principal">
        {NAV.map((sec) => (
          <div key={sec.group} className="mb-4">
            <p className="ops-label px-4 mb-1 !text-[10px] !text-[var(--muted2)]">{sec.group}</p>
            {sec.items.filter((i) => !i.ownerOnly || user?.permissions?.manage_users).map(({ to, end, label, icon: Icon, testid, alerts: showAlerts }) => (
              <NavLink key={to} to={to} end={end} data-testid={testid} onClick={onNavigate}
                className={({ isActive }) => `relative flex items-center gap-2.5 h-10 lg:h-8 mx-2 px-2.5 rounded-[var(--radius-sm)] text-[13px] transition-colors ${isActive ? "bg-[var(--surface2)] text-[var(--text)]" : "text-[var(--muted)] hover:text-[var(--text)]"}`}>
                {({ isActive }) => (
                  <>
                    {isActive && <span className="absolute -left-2 top-1.5 bottom-1.5 w-[2px] bg-[var(--accent)]" />}
                    <Icon size={15} aria-hidden="true" className={isActive ? "text-[var(--accent-ink)]" : ""} />
                    <span className="truncate">{label}</span>
                    {showAlerts && alerts > 0 && (
                      <span data-testid="nav-alerts-count" className="ml-auto mono text-[10px] px-1.5 rounded-[3px]" style={{ background: "rgba(226,102,94,0.15)", color: "var(--bad)" }}>{alerts}</span>
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div className="border-t border-[var(--border)] p-3">
        <p className="text-[12px] truncate">{user?.name || "Administrador"}</p>
        <p className="text-[11px] text-[var(--muted2)] truncate">{user?.email}</p>
        <p className="text-[10px] mono uppercase tracking-wider text-[var(--accent-ink)] mb-2" data-testid="sidebar-role">{user?.role_label}</p>
        <button onClick={onLogout} data-testid="logout-btn" className="ops-btn ops-btn-ghost ops-btn-sm w-full !justify-start">
          <LogOut size={14} /> Sair
        </button>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const reduce = useReducedMotion();
  const [open, setOpen] = useState(false);
  const alerts = useOpenAlerts();
  useStackTables();
  const canOperate = !!user?.permissions?.operate;
  useEffect(() => {
    if (!canOperate) return;
    api.post("/admin/links/sync-catalog", { links: PLATFORMS.map((p) => ({ key: p.id, url: p.link })) }).catch(() => {});
  }, [canOperate]);

  const doLogout = async () => { await logout(); navigate("/admin/login", { replace: true }); };
  const sidebar = <Sidebar onNavigate={() => setOpen(false)} alerts={alerts} user={user} onLogout={doLogout} />;

  return (
    <div className="mia-admin flex min-h-screen">
      <aside className="hidden lg:block w-60 shrink-0 bg-[var(--bg2)] border-r border-[var(--border)] sticky top-0 h-screen">{sidebar}</aside>

      <AnimatePresence>
        {open && (
          <div className="lg:hidden fixed inset-0 z-50">
            <motion.div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} />
            <motion.aside role="dialog" aria-label="Menu" className="relative w-[min(18rem,86vw)] h-full bg-[var(--bg2)] border-r border-[var(--border)]"
              initial={{ x: reduce ? 0 : -24, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: reduce ? 0 : -24, opacity: 0 }}
              transition={{ duration: 0.16, ease: [0.2, 0, 0, 1] }}>
              <button className="absolute right-2 top-3 ops-btn ops-btn-ghost ops-btn-sm" onClick={() => setOpen(false)} aria-label="Fechar menu" data-testid="mobile-menu-close"><X size={16} /></button>
              {sidebar}
            </motion.aside>
          </div>
        )}
      </AnimatePresence>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 flex items-center gap-3 px-4 sm:px-6 border-b border-[var(--border)] bg-[var(--bg)] sticky top-0 z-30">
          <div className="lg:hidden flex items-center gap-2">
            <button className="ops-btn ops-btn-ghost ops-btn-sm" onClick={() => setOpen(true)} data-testid="mobile-menu-btn" aria-label="Abrir menu"><Menu size={18} /></button>
            <BrandMark withName={false} size={24} />
          </div>
          <a href="/" target="_blank" rel="noopener noreferrer" data-testid="view-public-site"
            className="ml-auto inline-flex items-center gap-1.5 min-h-[38px] px-1 text-[12px] text-[var(--muted)] hover:text-[var(--text)]">
            Site público <ExternalLink size={12} />
          </a>
        </header>
        <main className="flex-1 p-4 sm:p-6 pb-[calc(5.5rem+env(safe-area-inset-bottom))] lg:pb-6">
          <div className="max-w-[1320px] mx-auto">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div key={location.pathname}
                initial={{ opacity: 0, y: reduce ? 0 : 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.14, ease: [0.2, 0, 0, 1] }}>
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </div>
        </main>
      </div>
      <BottomNav onMenu={() => setOpen(true)} alerts={alerts} />
    </div>
  );
}
